import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useParams } from "react-router-dom";
import BreadCrumb from "components/common/BreadCrumb";
import { getDocumentTitle } from "utils/helpers";
import {
  getStockLocation,
  getStockLocationStock,
} from "features/StockLocations/services/StockLocationService";
import type {
  StockLocation,
  StockLocationStockResponse,
  StockLocationStockRow,
} from "features/StockLocations/types/stock-location.types";
import { StockLocationTypeLabel } from "features/StockLocations/constants/stock-location.constants";
import moment from "moment";

function extractStockRows(
  data: StockLocationStockResponse | null | undefined,
): StockLocationStockRow[] {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data)) return data;
  const rows = (data as { rows?: StockLocationStockRow[] }).rows;
  return Array.isArray(rows) ? rows : [];
}

function stockQty(row: StockLocationStockRow): string {
  const v =
    row.availableQty ??
    row.onHandQty ??
    row.totalAvailableQty ??
    row.quantity;
  if (v == null || Number.isNaN(Number(v))) return "—";
  return String(v);
}

function productLabel(row: StockLocationStockRow): string {
  return (
    row.product?.name ??
    row.productName ??
    row.name ??
    row.sku ??
    row.product?.sku ??
    (row.productId != null ? `Product #${row.productId}` : "—")
  );
}

const ViewStockLocationDetails = () => {
  document.title = getDocumentTitle("Stock location");
  const { id } = useParams();
  const [record, setRecord] = useState<StockLocation | null>(null);
  const [stockRows, setStockRows] = useState<StockLocationStockRow[]>([]);
  const [stockLoaded, setStockLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getStockLocation(Number(id));
        if (!cancelled && res.success) {
          setRecord(res.data);
        }
      } catch (e) {
        console.warn(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id || activeTab !== "2") return;
    let cancelled = false;
    setStockLoaded(false);
    (async () => {
      try {
        const res = await getStockLocationStock(Number(id));
        if (!cancelled && res.success) {
          setStockRows(extractStockRows(res.data));
        }
      } catch (e) {
        console.warn(e);
        if (!cancelled) setStockRows([]);
      } finally {
        if (!cancelled) setStockLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, activeTab]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={record?.name ?? "Stock location"}
          pageTitle="Inventory Management"
          backTo={{
            label: "Stock locations",
            path: "/inventory-management/stock-locations",
          }}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <Nav className="nav-tabs nav-tabs-custom nav-success mb-3">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "1" })}
                      onClick={() => setActiveTab("1")}
                      style={{ cursor: "pointer" }}
                    >
                      Details
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "2" })}
                      onClick={() => setActiveTab("2")}
                      style={{ cursor: "pointer" }}
                    >
                      Stock
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <div className="table-responsive">
                      <table className="table mb-0">
                        <tbody>
                          <tr>
                            <th scope="row" style={{ width: 200 }}>
                              Name
                            </th>
                            <td>{record?.name ?? "—"}</td>
                          </tr>
                          <tr>
                            <th scope="row">Type</th>
                            <td>
                              {record?.type
                                ? StockLocationTypeLabel[record.type]
                                : "—"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">City</th>
                            <td>{record?.city?.trim() || "—"}</td>
                          </tr>
                          <tr>
                            <th scope="row">State</th>
                            <td>{record?.state?.trim() || "—"}</td>
                          </tr>
                          {record?.created_at ? (
                            <tr>
                              <th scope="row">Created</th>
                              <td>
                                {moment(record.created_at).format(
                                  "MMM D, YYYY h:mm A",
                                )}
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </TabPane>
                  <TabPane tabId="2">
                    {!stockLoaded ? (
                      <p className="text-muted mb-0">Loading stock…</p>
                    ) : stockRows.length === 0 ? (
                      <p className="text-muted mb-0">
                        No stock rows for this location.
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-nowrap align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Product</th>
                              <th>SKU</th>
                              <th className="text-end">Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockRows.map((row, idx) => (
                              <tr key={`${row.productId ?? idx}-${idx}`}>
                                <td>
                                  {row.product?.id != null ? (
                                    <Link
                                      className="fw-medium link"
                                      to={`/inventory-management/products/${row.product.id}`}
                                    >
                                      {productLabel(row)}
                                    </Link>
                                  ) : (
                                    productLabel(row)
                                  )}
                                </td>
                                <td className="text-muted">
                                  {row.product?.sku ?? row.sku ?? "—"}
                                </td>
                                <td className="text-end">{stockQty(row)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ViewStockLocationDetails;
